import React, {useMemo, useState} from "react"
import moment from "moment"
import {handleError, roundTwoDigits} from "helpers"
import SummaryView from "./SummaryView"
import {BaseRangePicker} from "components/DateTimePicker"
import styles from "./DailyAttendanceCalendarView.module.css"
import cx from "classnames"
import {attendanceService} from "services"
import {useQuery} from "@tanstack/react-query"
import {BasePopup} from "uiKit"
import {BaseButton, BaseInput, BaseLoading, SecondaryButton} from "components"
import {Checkbox, Col, InputNumber, Row} from "antd"
import {FormLabel} from "components/Form"
import {BaseDatePicker} from "components/DateTimePicker"
import {useCurrentProfilePermissions} from "hooks"
import {Permissions} from "@edular/permissions"

// Helper to create an array of month/year pairs between two dates
function getMonthsInRange(startDate, endDate) {
    const start = moment(startDate).startOf("month")
    const end = moment(endDate).startOf("month")
    const months = []
    let current = start.clone()
    while (current.isSameOrBefore(end)) {
        months.push(current.clone())
        current.add(1, "month")
    }
    return months
}

// Build days grid for a month (nulls for empty cells before 1st day)
function buildMonthDays(momentMonth) {
    const daysInMonth = momentMonth.daysInMonth()
    const startWeekday = momentMonth.startOf("month").day()

    const days = Array(startWeekday).fill(null) // empty slots for grid alignment
    for (let d = 1; d <= daysInMonth; d++) days.push(d)
    return days
}

// Format date like "YYYY-MM-DD" for event matching
function formatDateOnly(date) {
    return moment(date).format("YYYY-MM-DD")
}

const DailyAttendanceCalendarView = (payload: {
    studentProfileId: any
    campusId: number
    timezone?: string
    startDate?: string
    contractEndDate?: string
}) => {
    const {
        studentProfileId,
        campusId,
        timezone = moment.tz.guess(),
        startDate = moment().tz(timezone).subtract(2, "year").format("YYYY-MM-DD"),
        contractEndDate
    } = payload

    const projectedEndDate = useMemo(() => {
        return moment().tz(timezone)?.format("YYYY-MM-DD")
    }, [timezone])

    const allEndDate = projectedEndDate || contractEndDate
    const [dateRange, setDateRange] = useState(
        timezone ? [moment().tz(timezone).subtract(6, "days"), moment().tz(timezone)] : []
    )
    const [isAllDateRange, setIsAllDateRange] = useState(false)
    const [lastCustomDateRange, setLastCustomDateRange] = useState<any[]>([])
    const [hoveredDateStr, setHoveredDateStr] = useState<string | null>(null)
    const [selectedDateStr, setSelectedDateStr] = useState<string | null>(null)
    const [isEditPopupVisible, setIsEditPopupVisible] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [editValues, setEditValues] = useState({
        date: null,
        missedHours: 0,
        distEdHours: 0,
        makeUpHours: 0,
        attendedHours: 0,
        scheduleHours: 0,
        isLoa: false
    })
    const currentProfilePermissions = useCurrentProfilePermissions()
    const canEdit = currentProfilePermissions.hasPermissions({
        staff: [Permissions.Staff.AcademicAffairs.StudentServices.Attendance.Edit]
    })
    const canAdd = currentProfilePermissions.hasPermissions({
        staff: [Permissions.Staff.AcademicAffairs.StudentServices.Attendance.Add]
    })

    const allDateRange = useMemo(() => {
        if (!startDate || !allEndDate) return null
        return [moment(startDate).subtract(1, "year"), moment(allEndDate).add(1, "year")]
    }, [allEndDate, startDate])

    const getFilterParams = () => {
        const params: any = {
            studentProfileId
        }

        if (isAllDateRange && allDateRange) {
            params.fromDate = moment(allDateRange[0]).format("YYYY-MM-DD")
            params.toDate = moment(allDateRange[1]).format("YYYY-MM-DD")
        } else if (dateRange?.length === 2) {
            const start = moment(dateRange[0]).startOf("day")
            const end = moment(dateRange[1]).endOf("day")
            params.fromDate = start.format("YYYY-MM-DD")
            params.toDate = end.format("YYYY-MM-DD")
        }

        return params
    }

    const queryFromDate = useMemo(() => {
        if (isAllDateRange && allDateRange) return moment(allDateRange[0]).format("YYYY-MM-DD")
        if (dateRange?.length === 2) return moment(dateRange[0]).format("YYYY-MM-DD")
        return undefined
    }, [allDateRange, dateRange, isAllDateRange])

    const queryToDate = useMemo(() => {
        if (isAllDateRange && allDateRange) return moment(allDateRange[1]).format("YYYY-MM-DD")
        if (dateRange?.length === 2) return moment(dateRange[1]).format("YYYY-MM-DD")
        return undefined
    }, [allDateRange, dateRange, isAllDateRange])

    const {
        data: dailyAttendanceSummaryData,
        refetch,
        isFetching
    } = useQuery(
        ["attendance-summary-calendar-report", studentProfileId, queryFromDate, queryToDate],
        async () => {
            const params = getFilterParams()
            const {data} = await attendanceService.getDailyAttendanceSummaryCalendarReport(params)
            return data
        },
        {initialData: [], enabled: !!studentProfileId && !!queryFromDate && !!queryToDate}
    )

    const apiDateRange = useMemo(() => {
        if (!isAllDateRange) return null
        const dailyAttendances = (dailyAttendanceSummaryData?.dailyAttendances ?? []).filter(
            (item) =>
                item.attendedHours > 0 ||
                item.scheduleHours > 0 ||
                item.scheduleHours === "LOA" ||
                item.missedHours > 0 ||
                item.makeUpHours > 0 ||
                item.distEdHours > 0
        )
        if (!dailyAttendances.length) return null

        let minDate = null
        let maxDate = null
        for (const item of dailyAttendances) {
            if (!item?.date) continue
            const date = moment(item.date)
            if (!minDate || date.isBefore(minDate, "day")) minDate = date
            if (!maxDate || date.isAfter(maxDate, "day")) maxDate = date
        }

        if (!minDate || !maxDate) return null
        return [minDate, maxDate]
    }, [dailyAttendanceSummaryData, isAllDateRange])

    const displayDateRange = useMemo(() => {
        if (apiDateRange) return apiDateRange
        if (dateRange?.length !== 2) return null
        return [moment(dateRange[0]), moment(dateRange[1])]
    }, [apiDateRange, dateRange])

    const monthsInRange = useMemo(() => {
        if (!displayDateRange) return []
        return getMonthsInRange(displayDateRange[0], displayDateRange[1])
    }, [displayDateRange])

    // Process events grouped by date for fast lookup
    const eventsByDate = useMemo(() => {
        const dailyAttendances = dailyAttendanceSummaryData.dailyAttendances ?? []

        const map = {}
        for (const attendance of dailyAttendances) {
            const dateStr = formatDateOnly(attendance.date)
            if (!map[dateStr]) map[dateStr] = []

            const isLoa = attendance.scheduleHours === "LOA"

            map[dateStr].push({title: `Missed Hours: ${isLoa ? "" : roundTwoDigits(attendance.missedHours ?? 0)}`})
            map[dateStr].push({
                title: `DistEd Hours: ${isLoa ? "" : roundTwoDigits(attendance.distEdHours ?? 0)}`
            })
            map[dateStr].push({
                title: `Make-up Hours: ${isLoa ? "" : roundTwoDigits(attendance.makeUpHours ?? 0)}`
            })
            map[dateStr].push({
                title: `Attended Hours: ${isLoa ? "" : roundTwoDigits(attendance.attendedHours ?? 0)}`
            })
            map[dateStr].push({
                title: `Scheduled Hours: ${
                    isLoa ? attendance.scheduleHours : roundTwoDigits(attendance.scheduleHours ?? 0)
                }`
            })
        }
        return map
    }, [dailyAttendanceSummaryData])

    const attendanceByDate = useMemo(() => {
        const dailyAttendances = dailyAttendanceSummaryData.dailyAttendances ?? []
        const map = {}
        for (const attendance of dailyAttendances) {
            const dateStr = formatDateOnly(attendance.date)
            map[dateStr] = {
                missedHours: Number(attendance.missedHours ?? 0),
                distEdHours: Number(attendance.distEdHours ?? 0),
                makeUpHours: Number(attendance.makeUpHours ?? 0),
                attendedHours: Number(attendance.attendedHours ?? 0),
                scheduleHours: Number(attendance.scheduleHours ?? 0),
                isLoa: attendance.scheduleHours === "LOA"
            }
        }
        return map
    }, [dailyAttendanceSummaryData])

    const onCloseEditPopup = () => {
        setIsEditPopupVisible(false)
        setSelectedDateStr(null)
    }

    const onOpenEditPopup = (dateStr: string) => {
        setSelectedDateStr(dateStr)
        const existing = attendanceByDate?.[dateStr]

        if ((existing && canEdit) || (!existing && canAdd)) {
            setEditValues({
                date: moment(dateStr),
                missedHours: Number(existing?.missedHours ?? 0),
                distEdHours: Number(existing?.distEdHours ?? 0),
                makeUpHours: Number(existing?.makeUpHours ?? 0),
                attendedHours: Number(existing?.attendedHours ?? 0),
                scheduleHours: Number(existing?.scheduleHours ?? 0),
                isLoa: !!existing?.isLoa
            })
            setIsEditPopupVisible(true)
        }
    }

    const onConfirmEditPopup = async () => {
        try {
            setIsSubmitting(true)

            await attendanceService.createOrUpdateManualDailyAttendanceBydate({
                attendances: [
                    {
                        profileId: Number(studentProfileId),
                        date: moment(editValues.date).format("YYYY-MM-DD"),
                        attendedHours: Number(editValues.attendedHours ?? 0),
                        scheduleHours: Number(editValues.scheduleHours ?? 0),
                        makeUpHours: Number(editValues.makeUpHours ?? 0),
                        missedHours: Number(editValues.missedHours ?? 0),
                        distEdHours: Number(editValues.distEdHours ?? 0),
                        campusId
                    }
                ]
            })

            refetch()
            setIsEditPopupVisible(false)
            setSelectedDateStr(null)
        } catch (error) {
            handleError(error)
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div className={styles.container}>
            <div className={styles.pickerWrap}>
                <Checkbox
                    className={styles.allCheckbox}
                    checked={isAllDateRange}
                    disabled={!allDateRange}
                    onChange={(event) => {
                        const checked = event.target.checked
                        setIsAllDateRange(checked)
                        if (checked) {
                            if (dateRange?.length === 2) setLastCustomDateRange(dateRange)
                            if (allDateRange) setDateRange(allDateRange)
                        } else {
                            setDateRange(lastCustomDateRange?.length === 2 ? lastCustomDateRange : [])
                        }
                    }}>
                    ALL
                </Checkbox>
                <BaseRangePicker
                    placeholder={["Date From", "Date To"]}
                    isClearable
                    disabled={isAllDateRange}
                    value={dateRange?.length === 2 ? dateRange : null}
                    onChange={(value) => {
                        if (value) {
                            setIsAllDateRange(false)
                            setLastCustomDateRange(value)
                            setDateRange(value)
                        } else {
                            setIsAllDateRange(false)
                            setDateRange([])
                        }
                    }}
                />
            </div>
            {displayDateRange ? (
                <>
                    <div className={styles.monthsWrap}>
                        {monthsInRange.map((monthMoment) => {
                            const year = monthMoment.year()
                            const month = monthMoment.month()
                            const monthName = monthMoment.format("MMMM YYYY")
                            const days = buildMonthDays(monthMoment)

                            return (
                                <div key={`${year}-${month}`} className={styles.monthSection}>
                                    <h2 className={styles.monthTitle}>{monthName}</h2>
                                    <div className={styles.calendarGrid}>
                                        {/* Weekday headers */}
                                        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                                            <div key={d} className={styles.weekdayHeader}>
                                                {d}
                                            </div>
                                        ))}

                                        {/* Day cells */}
                                        {days.map((day, idx) => {
                                            if (!day) {
                                                return <div key={idx} className={styles.emptyCell} />
                                            }

                                            const dateStr = moment([year, month, day]).format("YYYY-MM-DD")
                                            const events = eventsByDate[dateStr] || []
                                            const isInRange =
                                                !!displayDateRange &&
                                                moment(dateStr).isBetween(
                                                    moment(displayDateRange[0]),
                                                    moment(displayDateRange[1]),
                                                    "day",
                                                    "[]"
                                                )
                                            const isHovered = isInRange && hoveredDateStr === dateStr

                                            return (
                                                <div
                                                    key={idx}
                                                    className={cx(styles.eventContainer, {
                                                        [styles.eventContainerDisabled]: !isInRange,
                                                        [styles.eventContainerHover]: isHovered,
                                                        [styles.eventContainerSelected]:
                                                            isInRange && selectedDateStr === dateStr
                                                    })}
                                                    onMouseEnter={() => setHoveredDateStr(dateStr)}
                                                    onMouseLeave={() => setHoveredDateStr(null)}
                                                    onClick={() => {
                                                        if (!isInRange) return
                                                        onOpenEditPopup(dateStr)
                                                    }}>
                                                    <div className={styles.day}>{day}</div>
                                                    <div className={styles.eventList}>
                                                        {isInRange &&
                                                            events.map((evt, i) => (
                                                                <div
                                                                    key={i}
                                                                    className={styles.eventItem}
                                                                    title={evt.title}>
                                                                    {evt.title}
                                                                </div>
                                                            ))}
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            )
                        })}
                    </div>

                    <SummaryView dailyAttendanceSummaryData={dailyAttendanceSummaryData} />
                </>
            ) : (
                <div className={styles.missingDateRangeMessage}>
                    Please select a date range to view the weekly attendance records.
                </div>
            )}

            <BasePopup
                isShow={isEditPopupVisible}
                onClose={onCloseEditPopup}
                width="520px"
                leftIcon="EDIT"
                leftIconColor="#fff">
                <div className={styles.popupRoot}>
                    <div className={styles.popupTitle}>
                        {attendanceByDate?.[selectedDateStr] ? "Update Daily Attendance" : "Create Daily Attendance"}
                    </div>
                    <Row gutter={[12, 12]} className={styles.popupForm}>
                        <Col span={24}>
                            <FormLabel label="Date" />
                            <BaseDatePicker value={editValues.date} disabled />
                        </Col>
                        <Col span={12}>
                            <FormLabel label="Missed Hours" />
                            <BaseInput
                                className={styles.inputNumber}
                                min={0}
                                type="number"
                                step={0.25}
                                disabled={!!editValues?.isLoa}
                                value={editValues.missedHours}
                                onChange={(value) =>
                                    setEditValues((prev) => ({...prev, missedHours: Number(value ?? 0)}))
                                }
                            />
                        </Col>
                        <Col span={12}>
                            <FormLabel label="DistEd Hours" />
                            <BaseInput
                                type="number"
                                className={styles.inputNumber}
                                min={0}
                                disabled={!!editValues?.isLoa}
                                value={editValues.distEdHours}
                                onChange={(value) =>
                                    setEditValues((prev) => ({...prev, distEdHours: Number(value ?? 0)}))
                                }
                            />
                        </Col>
                        <Col span={12}>
                            <FormLabel label="Make-up Hours" />
                            <BaseInput
                                className={styles.inputNumber}
                                min={0}
                                disabled={!!editValues?.isLoa}
                                type="number"
                                value={editValues.makeUpHours}
                                onChange={(value) =>
                                    setEditValues((prev) => ({...prev, makeUpHours: Number(value ?? 0)}))
                                }
                            />
                        </Col>
                        <Col span={12}>
                            <FormLabel label="Attended Hours" />
                            <BaseInput
                                type="number"
                                className={styles.inputNumber}
                                min={0}
                                disabled={!!editValues?.isLoa}
                                value={editValues.attendedHours}
                                onChange={(value) =>
                                    setEditValues((prev) => ({...prev, attendedHours: Number(value ?? 0)}))
                                }
                            />
                        </Col>
                        <Col span={24}>
                            <FormLabel label="Scheduled Hours" />
                            <BaseInput
                                type="number"
                                className={styles.inputNumber}
                                min={0}
                                disabled={!!editValues?.isLoa}
                                value={editValues.scheduleHours}
                                onChange={(value) =>
                                    setEditValues((prev) => ({...prev, scheduleHours: Number(value ?? 0)}))
                                }
                            />
                        </Col>
                    </Row>
                    <div className={styles.popupFooter}>
                        <div className={styles.popupButton}>
                            <SecondaryButton title="Cancel" onClick={onCloseEditPopup} disabled={isSubmitting} />
                        </div>
                        <div className={styles.popupButton}>
                            <BaseButton
                                title="Confirm"
                                onClick={onConfirmEditPopup}
                                loading={isSubmitting}
                                disabled={!!editValues?.isLoa}
                            />
                        </div>
                    </div>
                </div>
            </BasePopup>
            <BaseLoading isShow={isFetching || isSubmitting} />
        </div>
    )
}

export default DailyAttendanceCalendarView
